/**
 * AT-13 — InstallConsentSummary.
 *
 * Read-only summary block rendered identically by:
 *   - the Onboarding install-consent phase (`InstallConsentPhase.tsx`),
 *   - the Settings Disclosure tab.
 *
 * Carries no Continue button — the wrapping component renders the action
 * surface so the summary itself is action-free (the Settings Disclosure
 * tab is fully read-only).
 *
 * All copy strings come from `install-consent-copy.ts` so the two surfaces
 * stay byte-equivalent (AT-13 acceptance).
 */

import {
  HOW_IT_WORKS_URL,
  HOW_IT_WORKS_ANONYMITY_ANCHOR_URL,
  POLICY_JSON_GITHUB_URL,
  INSTALL_CONSENT_COPY,
  INSTALL_CONSENT_SHARES,
  INSTALL_CONSENT_VERSIONS,
} from './install-consent-copy';

export function InstallConsentSummary() {
  return (
    <section
      data-testid="install-consent-summary"
      className="space-y-5 text-neutral-900"
      aria-label="OwnYou install-consent summary"
    >
      <header>
        <h2 className="text-2xl font-semibold">{INSTALL_CONSENT_COPY.headline}</h2>
      </header>

      <div>
        <p className="text-base">{INSTALL_CONSENT_COPY.shareIntro}</p>
        <ul
          data-testid="install-consent-shares"
          className="mt-3 space-y-1.5 list-disc list-inside text-sm text-neutral-700"
        >
          {INSTALL_CONSENT_SHARES.map((share) => (
            <li key={share.label}>
              <span className="font-medium text-neutral-900">{share.label}</span>
              <span className="text-neutral-700"> — {share.detail}</span>
            </li>
          ))}
        </ul>
      </div>

      <p
        data-testid="install-consent-anonymity"
        className="text-sm leading-relaxed bg-amber-50 border-l-2 border-amber-400 p-3 rounded-r"
      >
        {INSTALL_CONSENT_COPY.anonymityStatement}{' '}
        {INSTALL_CONSENT_COPY.learnHowLabel} →{' '}
        <a
          data-testid="install-consent-learn-how"
          href={HOW_IT_WORKS_URL}
          className="text-primary-600 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          ownyou.app/how-it-works
        </a>
      </p>

      <div className="text-xs space-y-1 text-neutral-600 font-mono">
        <p data-testid="install-consent-disclosure-policy-version">
          Disclosure policy: {INSTALL_CONSENT_VERSIONS.disclosure_policy_version} →{' '}
          <a
            data-testid="install-consent-policy-json-link"
            href={POLICY_JSON_GITHUB_URL}
            className="text-primary-600 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {INSTALL_CONSENT_COPY.policyJsonLinkLabel}
          </a>
        </p>
        <p data-testid="install-consent-trust-registry-version">
          Trust registry: {INSTALL_CONSENT_VERSIONS.trust_registry_version}
        </p>
        <p data-testid="install-consent-protocol-version">
          Protocol version: {INSTALL_CONSENT_VERSIONS.protocol_version}
        </p>
      </div>

      <p data-testid="install-consent-defaults-note" className="text-sm text-neutral-700">
        {INSTALL_CONSENT_COPY.defaultsNote}{' '}
        <a
          data-testid="install-consent-why-defaults-link"
          href={HOW_IT_WORKS_ANONYMITY_ANCHOR_URL}
          className="text-primary-600 underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {INSTALL_CONSENT_COPY.whyDefaultsLinkLabel}
        </a>
      </p>

      <p data-testid="install-consent-testnet-note" className="text-sm text-neutral-700">
        {INSTALL_CONSENT_COPY.testnetNote}
      </p>
    </section>
  );
}
